import axios from 'axios'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { showToast, showFailToast } from 'vant'
import { useUserStore } from '@/store/modules/user'

/**
 * HTTP请求工具
 * 功能：封装axios，处理请求拦截、响应拦截、错误处理
 */

// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
})

/**
 * API 前缀配置
 * 可以根据需要添加更多的路由规则
 */
const API_PREFIX_CONFIG = {
  // 管理员相关接口使用 admin-api
  admin: ['system/auth/login'],
}

/**
 * 根据请求路径判断使用哪个 API 前缀
 * @param url 请求路径
 * @returns API 前缀
 */
function getApiPrefix(url: string): string {
  // 检查是否是管理员接口
  const isAdminApi = API_PREFIX_CONFIG.admin.some(prefix => url.includes(prefix))

  if (isAdminApi) {
    return '/admin-api'
  }

  // 默认使用 app-api
  return import.meta.env.VITE_APP_BASE_API
}

/**
 * 请求拦截器
 * 自动添加token到请求头，动态设置API前缀
 */
service.interceptors.request.use(
  config => {
    // 动态设置 API 前缀
    const apiPrefix = getApiPrefix(config.url || '')
    config.url = apiPrefix + (config.url || '')

    console.log('请求URL:', config.url) // 调试日志

    // 获取销售人员token
    const salesToken = localStorage.getItem('salesToken')
    // 获取用户token
    const userToken = localStorage.getItem('userToken')

    // 优先使用用户token，如果没有则使用销售人员token
    const token = userToken || ''
    const isAdminApi = API_PREFIX_CONFIG.admin.some(prefix => config.url?.includes(prefix))
    if (token && !isAdminApi) {
      config.headers.Authorization = `${token}`
    }

    return config
  },
  error => {
    console.error('请求错误：', error)
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器
 * 处理响应数据和错误状态码
 */
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data

    // 根据业务状态码处理响应
    if (res.code !== 0 && res.code !== 200) {
      showFailToast(res.message || res.msg || '操作失败')

      // 处理token过期或无效的情况
      if (res.code === 401) {
        handleTokenExpired()
      }

      return Promise.reject(new Error(res.message || res.msg || '操作失败'))
    } else {
      // 返回业务数据
      return res.data
    }
  },
  error => {
    console.error('响应错误：', error)
    let message = error.message || '网络异常，请稍后重试'

    if (error.response) {
      switch (error.response.status) {
        case 400:
          message = '请求参数错误'
          break
        case 401:
          message = '登录已过期，请重新登录'
          handleTokenExpired()
          break
        case 403:
          message = '没有权限访问'
          localStorage.removeItem('salesToken')
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        case 502:
          message = '网关错误'
          break
        case 503:
          message = '服务暂时不可用'
          break
        default:
          message = '服务器错误'
          break
      }
    }

    showFailToast(message)
    return Promise.reject(error)
  }
)

/**
 * 处理token过期的情况
 * 清除本地存储的token并跳转到登录页
 */
function handleTokenExpired() {
  const userStore = useUserStore()
  // 清除所有token
  localStorage.removeItem('salesToken')
  localStorage.removeItem('userToken')
  userStore.logoutAction()
  // 延迟跳转，避免在拦截器中直接操作路由
  setTimeout(() => {
    // 跳转到销售登录页
    window.location.href = '/user-login'
  }, 1000)
}

/**
 * 封装GET请求
 * @param url 请求地址
 * @param params 请求参数
 * @param config 请求配置
 * @returns Promise<T> 返回响应数据
 */
export function get<T>(url: string, params?: any, config: AxiosRequestConfig = {}): Promise<T> {
  return service.get(url, { params, ...config })
}

/**
 * 封装POST请求
 * @param url 请求地址
 * @param data 请求数据
 * @param config 请求配置
 * @returns Promise<T> 返回响应数据
 */
export function post<T>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<T> {
  return service.post(url, data, config)
}

/**
 * 封装PUT请求
 * @param url 请求地址
 * @param data 请求数据
 * @param config 请求配置
 * @returns Promise<T> 返回响应数据
 */
export function put<T>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<T> {
  return service.put(url, data, config)
}

/**
 * 封装DELETE请求
 * @param url 请求地址
 * @param config 请求配置
 * @returns Promise<T> 返回响应数据
 */
export function del<T>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
  return service.delete(url, config)
}

export default service
